/**
 * auditResource — the operator audit-ledger read surface (#944), authored
 * against the resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A1).
 *
 * The ledger is READ-ONLY: one row per privileged HTTP call
 * (method / route_path / permission / outcome / trace_id / actor / created_at),
 * paged through the shared list kernel. There is no create/edit/delete, no
 * bulk/row action, and no `/:id` route — so:
 *   - `detail.surface: 'drawer'` (row click opens the Sheet; no full-page link);
 *   - the four hand-rolled filter inputs collapse into the framework's derived
 *     QueryBuilder, which carries every key of `AUDIT_FILTERS`: `action`
 *     (route_path, `eq`), `actor` (`eq`), and `created_at`
 *     (`from` / `to` / `between`). No quick filter sits beside it;
 *   - the free-text search box stays suppressed because the derived filter
 *     schema (from `filterable` = `AUDIT_FILTERS`) is non-empty and the ledger
 *     400s on the reserved `search` param (裁决 9). `auditAPI.list` strips
 *     `search` at the service layer as a second guard.
 *
 * Every cell projection lives in `./widgets.tsx`. That file is where the three
 * legacy carriers this resource used — `render`, `transform` and `copyable` —
 * landed, and its header says why each needed a different member of `Widget`;
 * the `copyable` → `auditTraceId` case in particular is NOT a free swap, so
 * read it before touching the trace-id column.
 *
 * Route-gated by `<AdminGuard>` in `App.tsx` (backend gate is
 * `requireAuth + requireAdmin` on `/api/audit-log`), so no `permission` block.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type EnumOption,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { AUDIT_LOG_SORTS } from '@services/filters.generated';
import { AUDIT_FILTERS, AUDIT_OUTCOME, auditAPI, type AuditLog } from '@services/audit';
import { toListResult } from '@services/list-envelope';

/**
 * The closed outcome set from `services/audit.ts`; labels are i18n keys the
 * renderer resolves and `color` is the Badge variant it resolves through
 * `enumBadgeVariant`. Both live on the MODEL so the widget reads them off
 * `field.options` and holds no second copy — see `./widgets.tsx` for why that
 * matters beyond tidiness.
 */
const OUTCOME_OPTIONS: EnumOption[] = [
  { value: AUDIT_OUTCOME.OK, label: 'audit.outcome.ok', color: 'success' },
  { value: AUDIT_OUTCOME.ERROR, label: 'audit.outcome.error', color: 'destructive' },
];

const f = createFieldConstructors<AuditLog>();

export const auditModel = defineModel<AuditLog>()({
  name: 'audit',
  idField: 'id',
  recName: 'route_path',
  fields: {
    created_at: f.datetime({
      label: 'audit.colTimestamp',
      // Filterable through the QueryBuilder like every other column, and
      // through NOTHING ELSE. It used to be `filterable: false` behind a
      // bespoke daterange quick filter, which put the field an operator
      // filters by most often in the one place the condition row could
      // not reach. The whitelist intersection is therefore all THREE of
      // `AUDIT_FILTERS`' keys, not the `actor` + `action` pair.
      widget: 'auditTimestamp',
      // FORENSIC (#2105): an audit row is read alongside a Worker log line, a
      // provider's dashboard and an incident timeline, all of which are UTC.
      // Reading a bare filter day as a merchant business day would silently
      // slide the window seven or eight hours away from every one of them —
      // precisely when someone is trying to line up what happened when.
      instantReading: 'utc',
    }),
    // Real accessor key for the row snapshot; the `actor` FILTER fans out to
    // actor_id / actor_label server-side, so map the column to it via
    // `filterId`. The widget resolves the display label for both the cell
    // and the CSV export, off the record.
    actor_label: f.text({
      label: 'audit.colActor',
      filterId: 'actor',
      widget: 'auditActor',
    }),
    method: f.text({
      label: 'audit.colMethod',
      filterable: false,
      widget: 'auditMonoText',
    }),
    // The route filter is pinned to the `action` whitelist field (`eq` only).
    route_path: f.text({
      label: 'audit.colRoute',
      filterId: 'action',
      widget: 'auditMonoText',
    }),
    outcome: f.enum({
      label: 'audit.colOutcome',
      options: OUTCOME_OPTIONS,
      filterable: false,
      widget: 'auditOutcome',
    }),
    // Detail-only fields (hidden in the list, shown in the drawer) — the
    // request path, permission, error message, and trace id the bespoke
    // Sheet used to surface.
    request_path: f.text({
      label: 'audit.requestPath',
      filterable: false,
      widget: 'auditMonoTextWrap',
    }),
    permission: f.text({
      label: 'audit.permission',
      filterable: false,
      widget: 'auditMonoTextWrap',
    }),
    error_message: f.text({
      label: 'audit.errorMessage',
      filterable: false,
    }),
    // Stays `text`, NOT `id`: the type is what the CSV export falls through
    // to once the widget declares no `csvText`, and the id type exports an
    // empty value as `''` where this column must keep exporting `-`.
    trace_id: f.text({
      label: 'audit.traceId',
      filterable: false,
      widget: 'auditTraceId',
    }),
  },
});

export const auditListView = defineListView(auditModel, {
  items: [
    { field: 'created_at' },
    { field: 'actor_label' },
    { field: 'method' },
    { field: 'route_path' },
    { field: 'outcome' },
    { field: 'request_path', columnInvisible: true },
    { field: 'permission', columnInvisible: true },
    { field: 'error_message', columnInvisible: true },
    { field: 'trace_id', columnInvisible: true },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
  // `capabilities.search` is deliberately absent, not `false`: suppression is
  // a CONSEQUENCE here (a non-empty derived filter schema hides the box), and
  // declaring it would claim a switch this resource does not own.
});

/** Read-only ledger: the drawer shows the same columns; there is no form. */
export const auditFormView = defineFormView(auditModel, { groupDisplay: 'tab', children: [] });

export const auditResource: ResourceBundle<AuditLog, typeof auditModel.fields> = {
  model: auditModel,
  list: auditListView,
  form: auditFormView,
  host: {
    api: {
      // Repoint through the service: `auditAPI.list` strips the reserved
      // `search` param and whitelists filters against `AUDIT_FILTERS`,
      // returning the `{ items, total, page, size }` list body whose kernel
      // `size` this adapter renames to the framework's `limit` (#2167 PR-2).
      list: async (params) => toListResult(await auditAPI.list(params)),
    },
    label: 'audit.pageTitle',
    slug: 'audit',
    listTitle: 'audit.pageTitle',
    // The line that distinguishes this ledger from the change ledger. It
    // is a fact about the LEDGER (requests, not data), so it rides on the
    // resource — the page only decides where a subtitle is painted.
    listDescription: 'audit.tab.requests.description',
    // The backend filter whitelist. Its presence makes the derived
    // QueryBuilder schema non-empty, which suppresses the free-text search
    // box (the ledger has no `search` column).
    filterable: AUDIT_FILTERS,
    sortable: AUDIT_LOG_SORTS,
    detail: { surface: 'drawer' },
  },
};
